import { ApolloClient, createHttpLink, InMemoryCache, from } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import router from './router';
import { pinia } from './stores/pinia';
import { useAuthStore } from './stores/auth';
import { pushToast } from '@/lib/toast';

export const performLogout = async (reason?: string) => {
  if (reason === 'expired') {
    pushToast('Session expired. Please log in again.', 'error', 5000);
  }

  useAuthStore(pinia).clearSession();
  await apolloClient.clearStore();

  if (router.currentRoute.value.path !== '/login') {
    router.push('/login');
  }
};

const httpLink = createHttpLink({
  uri: process.env.VUE_APP_APOLLO_URL,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const isAuthError =
        err.message.includes('Signature verification failed') ||
        err.message.includes('Signature has expired') ||
        err.message.includes('Could not validate') ||
        err.message.includes('User does not exist') ||
        err.extensions?.code === 'UNAUTHENTICATED';

      if (isAuthError) {
        console.warn('[Apollo Error] Unauthorized access detected, logging out...');
        performLogout('expired');
      }
    }
  }
  if (networkError && (networkError as any).statusCode === 401) {
    performLogout();
  }
});

const authLink = setContext((_, { headers }) => {
  const token = useAuthStore(pinia).token;
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

export const apolloClient = new ApolloClient({
  link: from([authLink, errorLink, httpLink]),
  cache: new InMemoryCache(),
});
